import { auth, signOut } from '../config/firebaseConfig';

const TOKEN_STORAGE_PREFIX = 'app_auth_';
const API_URL = process.env.NEXT_PUBLIC_BACKEND_URL || process.env.NEXT_PUBLIC_BACK_URL;

class AuthService {
  // Получение токенов
  getAccessToken() {
    return typeof window !== 'undefined' 
      ? localStorage.getItem(`${TOKEN_STORAGE_PREFIX}access_token`) 
      : null;
  }

  getRefreshToken() {
    return typeof window !== 'undefined' 
      ? localStorage.getItem(`${TOKEN_STORAGE_PREFIX}refresh_token`) 
      : null;
  }

  // Получение Firebase токена (используется ТОЛЬКО для обмена на JWT)
  async getFirebaseToken(forceRefresh = false) {
    if (!auth.currentUser) return null;
    
    try {
      return await auth.currentUser.getIdToken(forceRefresh);
    } catch (error) {
      console.error('Error getting Firebase token:', error);
      return null;
    }
  }

  // Сохранение токенов (в localStorage и куки)
  setTokens(accessToken, refreshToken, expiresIn) {
    if (typeof window === 'undefined') return;
    
    // Сохраняем в localStorage
    localStorage.setItem(`${TOKEN_STORAGE_PREFIX}access_token`, accessToken);
    localStorage.setItem(`${TOKEN_STORAGE_PREFIX}refresh_token`, refreshToken);
    
    // Устанавливаем куки для работы с сервером
    this.setCookie('jwt_access_token', accessToken, expiresIn);
    this.setCookie('jwt_refresh_token', refreshToken, 30 * 24 * 60 * 60); // 30 дней
  }

  // Вспомогательная функция для установки куки
  setCookie(name, value, maxAgeInSeconds) {
    if (typeof document === 'undefined') return;
    
    const cookieOptions = [
      `${name}=${value}`,
      'path=/',
      maxAgeInSeconds > 0 ? `max-age=${maxAgeInSeconds}` : '',
      'SameSite=Lax'
    ].filter(Boolean).join('; ');
    
    document.cookie = cookieOptions;
  }

  // Очистка токенов
  clearTokens() {
    if (typeof window === 'undefined') return;
    
    // Очищаем localStorage
    try {
      localStorage.removeItem(`${TOKEN_STORAGE_PREFIX}access_token`);
      localStorage.removeItem(`${TOKEN_STORAGE_PREFIX}refresh_token`);
    } catch (e) {
      console.error('Error clearing localStorage:', e);
    }
    
    // Очищаем все возможные куки авторизации
    try {
      if (typeof document !== 'undefined') {
        const cookiesToClear = [
          'jwt_access_token',
          'jwt_refresh_token',
          'auth_token',
          'firebase_token',
          'id_token',
          'token'
        ];
        
        cookiesToClear.forEach(name => {
          // Очищаем куки для всех возможных путей и доменов
          document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
          
          // Дополнительная очистка для корневого домена
          const domain = window.location.hostname;
          if (domain.includes('.')) {
            const rootDomain = domain.split('.').slice(-2).join('.');
            document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=.${rootDomain}; SameSite=Lax`;
          }
        });
      }
    } catch (e) {
      console.error('Error clearing cookies:', e);
    }
  }

  // Обмен Firebase токена на JWT
  async exchangeFirebaseToken() {
    if (!auth.currentUser) {
      return null;
    }

    try {
      const firebaseToken = await this.getFirebaseToken(true);
      
      if (!firebaseToken) {
        return null;
      }
      
      const response = await fetch(`${API_URL}/auth/exchange-token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${firebaseToken}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Token exchange error:', response.status, errorText);
        return null;
      }
      
      const data = await response.json();
      
      // Сохраняем полученные токены
      this.setTokens(
        data.access_token,
        data.refresh_token,
        data.expires_in
      );
      
      return {
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresIn: data.expires_in,
        user: data.user
      };
    } catch (error) {
      console.error('Error during token exchange:', error);
      return null;
    }
  }

  // Обновление токена
  async refreshToken() {
    const refreshToken = this.getRefreshToken();
    
    if (!refreshToken) {
      return null;
    }
    
    try {
      const response = await fetch(`${API_URL}/auth/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        },
        body: JSON.stringify({ refresh_token: refreshToken })
      });
      
      if (!response.ok) {
        // Только ошибки не 2xx заслуживают лога
        if (response.status !== 401) { // 401 - стандартная ошибка при устаревшем токене
          console.error('Token refresh error:', response.status);
        }
        // При ошибке обновления очищаем токены
        this.clearTokens();
        return null;
      }
      
      const data = await response.json();
      
      // Сохраняем новые токены
      this.setTokens(
        data.access_token,
        data.refresh_token,
        data.expires_in
      );
      
      return {
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresIn: data.expires_in,
        user: data.user
      };
    } catch (error) {
      console.error('Error during token refresh:', error);
      this.clearTokens();
      return null;
    }
  }

  // Получение валидного токена (используется существующий или обновляет)
  async getValidToken() {
    const accessToken = this.getAccessToken();
    
    if (accessToken) {
      try {
        // Проверяем текущий токен
        const statusResponse = await fetch(`${API_URL}/auth/status`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache'
          }
        });
        
        if (statusResponse.ok) {
          return accessToken; // Текущий токен валиден
        }
      } catch (error) {
        // Ошибку игнорируем и продолжаем обновление
      }
    }
    
    // Пробуем обновить токен
    const refreshResult = await this.refreshToken();
    if (refreshResult) {
      return refreshResult.accessToken;
    }
    
    // Если обновление не удалось, пытаемся обменять Firebase токен
    const exchangeResult = await this.exchangeFirebaseToken();
    return exchangeResult ? exchangeResult.accessToken : null;
  }

  // Выполнение запроса с авторизацией
  async fetchWithAuth(url, options = {}) {
    // Проверяем, требуется ли авторизация для этого запроса
    const requireAuth = options.requireAuth !== false;
    
    // Удаляем параметр requireAuth из options перед отправкой запроса
    const { requireAuth: _, ...cleanOptions } = options;
    
    let token = null;
    
    // Получаем JWT токен только если требуется авторизация
    if (requireAuth) {
      token = await this.getValidToken();
      
      if (!token) {
        throw new Error('No valid JWT auth token available');
      }
    }
    
    // Создаем объект с заголовками
    const authOptions = {
      ...cleanOptions,
      headers: {
        ...cleanOptions.headers,
        'Content-Type': cleanOptions.headers?.['Content-Type'] || 'application/json'
      }
    };
    
    // Добавляем заголовок Authorization только если есть токен
    if (token) {
      authOptions.headers['Authorization'] = `Bearer ${token}`;
    }
    
    return fetch(url, authOptions);
  }

  // Выполнение запроса с обновлением токена при необходимости
  async fetchWithTokenRefresh(url, options = {}) {
    try {
      // Передаем параметр requireAuth в fetchWithAuth
      return await this.fetchWithAuth(url, options);
    } catch (error) {
      // Если запрос не требует авторизации, просто пробрасываем ошибку дальше
      if (options.requireAuth === false) {
        throw error;
      }
      
      // Если требуется авторизация, пробуем обновить токен и повторить запрос
      const token = await this.refreshToken();
      if (!token) {
        throw new Error('Unable to refresh auth token');
      }
      
      // Повторяем запрос с обновленным токеном
      return this.fetchWithAuth(url, options);
    }
  }

  // Проверка статуса авторизации
  async checkAuthStatus() {
    const token = await this.getValidToken();
    
    if (!token) {
      return { authenticated: false, user: null };
    }
    
    try {
      const response = await fetch(`${API_URL}/auth/status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        }
      });
      
      if (!response.ok) {
        return { authenticated: false, user: null };
      }
      
      const data = await response.json();
      return {
        authenticated: data.authenticated,
        user: data.user,
        expiresIn: data.expires_in
      };
    } catch (error) {
      console.error('Error checking auth status:', error);
      return { authenticated: false, user: null };
    }
  }

  // ЦЕНТРАЛИЗОВАННЫЙ МЕТОД ДЛЯ ВЫХОДА ИЗ СИСТЕМЫ (только фронтенд-очистка корзины)
  async unifiedLogout(options = {}) {
    const { 
      redirectUrl = null, 
      clearCart = true,
      notifyServer = true 
    } = options;
    
    try {
      // 1. Устанавливаем флаги ручного выхода для обработчиков
      if (typeof window !== 'undefined') {
        localStorage.setItem('manual_logout', 'true');
        localStorage.setItem('force_logout', 'true');
      }
      
      // 2. Вызываем серверный logout если нужно
      if (notifyServer) {
        const token = this.getAccessToken();
        if (token) {
          try {
            const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || process.env.NEXT_PUBLIC_BACK_URL;
            
            await fetch(`${backendUrl}/auth/logout`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
          } catch (e) {
            // Игнорируем ошибки серверного логаута
            console.warn('[AuthService] Server logout error (continuing):', e);
          }
        }
      }
      
      // 3. Очищаем корзину ТОЛЬКО на клиенте, если запрошено
      if (clearCart && typeof window !== 'undefined') {
        try {
          // Динамически импортируем хранилище
          const cartStore = (await import('../store/cartStore')).default.getState();
          
          // Очищаем корзину через специальную функцию handleLogout (только фронтенд)
          if (cartStore && typeof cartStore.handleLogout === 'function') {
            await cartStore.handleLogout();
            
            // ДОПОЛНИТЕЛЬНО убедимся, что корзина действительно пуста на фронте
            if (cartStore.cartItems && cartStore.cartItems.length > 0) {
              console.warn('[AuthService] Cart still has items after handleLogout, forcing clear on frontend');
              
              // Принудительно очищаем локальное состояние
              cartStore.setCartItems([]);
            }
          } else {
            console.warn('[AuthService] Cart store or handleLogout not available');
            
            // Принудительно очищаем localStorage на всякий случай
            if (typeof window !== 'undefined') {
              try {
                localStorage.removeItem('cart-storage');
              } catch (e) {
                console.error('[AuthService] Error removing cart storage:', e);
              }
            }
          }
        } catch (cartError) {
          console.error('[AuthService] Error handling cart during logout:', cartError);
        }
      }
      
      // 4. Очищаем токены и куки
      this.clearTokens();
      
      // 5. Очищаем основные данные авторизации
      try {
        if (typeof window !== 'undefined') {
          localStorage.removeItem('firebase:authUser:' + process.env.NEXT_PUBLIC_FIREBASE_API_KEY + ':[DEFAULT]');
          localStorage.removeItem('user');
          localStorage.removeItem('auth');
          localStorage.removeItem('user-storage');
          localStorage.removeItem('cart-storage'); // Гарантированно удаляем хранилище корзины
          sessionStorage.removeItem('backend_verified');
          localStorage.removeItem('auth_action');
          
          // Явно очищаем куки через document.cookie
          const cookiesToClear = [
            'jwt_access_token', 
            'jwt_refresh_token', 
            'auth_token', 
            'firebase_token',
            'id_token',
            'token'
          ];
          
          cookiesToClear.forEach(name => {
            document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
            
            // Очистка для корневого домена
            const domain = window.location.hostname;
            if (domain.includes('.')) {
              const rootDomain = domain.split('.').slice(-2).join('.');
              document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=.${rootDomain}; SameSite=Lax`;
            }
          });
        }
      } catch (e) {
        console.error('[AuthService] Error clearing additional auth data:', e);
      }
      
      // 6. Выходим из Firebase
      try {
        if (auth && auth.currentUser) {
          await signOut(auth);
        }
      } catch (signOutError) {
        console.error('[AuthService] Error during Firebase signOut:', signOutError);
      }
      
      // 7. Перенаправление или перезагрузка страницы без добавления метки времени
      const performRedirectOrReload = () => {
        if (typeof window !== 'undefined' && (redirectUrl || options.reload !== false)) {
          if (redirectUrl) {
            window.location.href = redirectUrl;
          } else {
            window.location.href = window.location.pathname + window.location.search;
          }
        }
      };
      
      // Задержка перед перезагрузкой
      if (typeof window !== 'undefined' && (redirectUrl || options.reload !== false)) {
        setTimeout(performRedirectOrReload, 300);
      }
      
      return true;
    } catch (error) {
      console.error('[AuthService] Critical error during logout:', error);
      
      // Даже при критической ошибке пытаемся очистить токены
      try {
        this.clearTokens();
        
        // И пытаемся перезагрузить страницу с задержкой
        if (typeof window !== 'undefined' && options.reload !== false) {
          setTimeout(() => {
            window.location.reload();
          }, 300);
        }
      } catch (e) {
        console.error('[AuthService] Failed to clean up after error:', e);
      }
      
      return false;
    }
  }

  // ОБНОВЛЕННЫЕ СУЩЕСТВУЮЩИЕ МЕТОДЫ, ИСПОЛЬЗУЯ ЦЕНТРАЛИЗОВАННУЮ ФУНКЦИЮ
  
  // Выход из системы (без перезагрузки страницы)
  async logout() {
    return this.unifiedLogout({ 
      notifyServer: true, 
      clearCart: true, 
      reload: false 
    });
  }

  // Централизованный выход с обязательной перезагрузкой страницы
  async logoutAndRedirect(redirectUrl = null) {
    return this.unifiedLogout({ 
      redirectUrl, 
      notifyServer: true, 
      clearCart: true, 
      reload: true 
    });
  }
}
 
const authService = new AuthService();
export default authService;